import React, { memo, useState, useEffect } from 'react';
import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Rate {
  sessionType: string;
  amount: number;
}

interface Therapist {
  _id?: string;
  firstName: string;
  lastName: string;
  title: string;
  slug?: string;
  address?: {
    city?: string;
    state?: string;
    zip?: string;
  };
  locations?: {
    state: string;
    office: string;
    virtual: boolean;
    timezone?: string;
  }[];
  specialties?: string[];
  verificationStatus: string;
  billingStatus: string;
  availability?: {
    openSpots?: number;
  };
  caseLoad?: {
    currentClients: number;
    maxCapacity: number;
  };
  profile?: {
    rates?: Rate[];
    bio?: string;
  };
  rating?: number;
  reviewCount?: number;
  urgencyLevel?: string;
  licenseYear?: number;
  weeklySessions?: number;
  practicedSince?: number;
}

interface TherapistCardProps {
  therapist: Therapist;
  index: number;
  /** In list layout, show bio expanded by default so notes are visible without an extra tap */
  variant?: 'gallery' | 'list';
}



// Main therapist card component
const TherapistCard = memo(({ therapist, variant = 'gallery' }: TherapistCardProps) => {
  const navigate = useNavigate();
  const [showBio, setShowBio] = useState(() => variant === 'list');

  useEffect(() => {
    if (variant === 'list') setShowBio(true);
  }, [variant]);

  const initials = `${therapist.firstName?.[0] || ''}${therapist.lastName?.[0] || ''}`;
  const fullName = `${therapist.title || ''} ${therapist.firstName || ''} ${therapist.lastName || ''}`.trim();
  const specialtiesText = therapist.specialties?.join(', ') || '';

  // Get individual rate for display
  const individualRate = therapist.profile?.rates?.find(rate => rate.sessionType === 'individual')?.amount;

  // Calculate years of experience
  const yearsExp = therapist.practicedSince
    ? new Date().getFullYear() - therapist.practicedSince
    : (therapist.licenseYear ? new Date().getFullYear() - therapist.licenseYear : 0);

  const locationStr = therapist.locations?.[0]
    ? `${therapist.locations[0].state}${therapist.locations[0].timezone ? ` (${therapist.locations[0].timezone})` : ''}`
    : therapist.address ? `${therapist.address.city}, ${therapist.address.state}` : '';

  // Mock data for demo - replace with actual data from your backend
  const rating = 15; // percentage
  const nextAvailable = "Monday at 9:00 AM"; // This should come from availability data

  const handleViewProfile = () => {
    if (therapist.slug) {
      navigate(`/clinician/${therapist.slug}`);
    }
  };

  const toggleBio = () => {
    setShowBio(!showBio);
  };

  return (
    <div className="p-4 sm:p-6 border border-white/20 rounded-xl hover:shadow-lg transition-all duration-300 bg-white/80 backdrop-blur-sm hover:bg-white/90 hover:scale-[1.02] group">
      {/* Mobile Layout */}
      <div className="block sm:hidden">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg flex items-center justify-center text-gray-700 font-semibold text-sm shadow-sm">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 text-sm">{fullName}</h4>
            <p className="text-xs text-gray-600">
              {therapist.title} • {yearsExp > 0 ? `${yearsExp} yrs exp` : ''}
              {locationStr && ` • ${locationStr}`}
            </p>
            <p className="text-xs text-gray-500 mt-1">{specialtiesText}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-purple-600" />
                <span className="text-xs font-semibold text-purple-600">{rating}%</span>
              </div>
              {therapist.weeklySessions && (
                <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                  {therapist.weeklySessions} sess/wk
                </span>
              )}
              {therapist.urgencyLevel && therapist.urgencyLevel !== 'None' && (
                <span className={`text-xs px-1.5 py-0.5 rounded ${therapist.urgencyLevel === 'Highest' || therapist.urgencyLevel === 'High'
                  ? 'bg-red-50 text-red-600'
                  : 'bg-green-50 text-green-600'
                  }`}>
                  {therapist.urgencyLevel}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {individualRate ? `$${individualRate}/session` : ''} • {nextAvailable}
            </p>
          </div>
        </div>
        {/* Bio Section */}
        {therapist.profile?.bio && (
          <div className="mt-3 border-t border-gray-100/50 pt-3">
            <button
              onClick={toggleBio}
              className="flex items-center justify-between w-full text-xs text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span>View Bio</span>
              {showBio ? (
                <ChevronUp className="w-3 h-3 transition-transform" />
              ) : (
                <ChevronDown className="w-3 h-3 transition-transform" />
              )}
            </button>
            {showBio && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-700 leading-relaxed">{therapist.profile.bio}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          {therapist.slug && (
            <button
              onClick={handleViewProfile}
              className="flex-1 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs py-2 px-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              View Profile
            </button>
          )}
          <button className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-xs py-2 px-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
            Select Clinician
          </button>
        </div>
      </div>

      {/* Desktop Grid Layout */}
      <div className="hidden sm:grid sm:grid-cols-12 gap-4">
        {/* Avatar Column */}
        <div className="col-span-2">
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl flex items-center justify-center text-gray-700 font-semibold text-sm shadow-sm">
            {initials}
          </div>
        </div>

        {/* Main Content Column */}
        <div className="col-span-7">
          <h4 className="font-semibold text-gray-900 text-sm mb-1">{fullName}</h4>
          <p className="text-xs text-gray-600 mb-2">
            {therapist.title} • {yearsExp > 0 ? `${yearsExp} years exp` : ''}
            {therapist.licenseYear && ` • Lic. ${therapist.licenseYear}`}
            {locationStr && ` • ${locationStr}`}
          </p>
          <p className="text-xs text-gray-500 mb-3">{specialtiesText}</p>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Star className="w-3 h-3 text-purple-600" />
              <span className="text-xs font-semibold text-purple-600">{rating}% Match</span>
            </div>

            {therapist.weeklySessions && (
              <div className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                {therapist.weeklySessions} sessions/wk
              </div>
            )}

            {therapist.urgencyLevel && therapist.urgencyLevel !== 'None' && (
              <div className={`text-xs px-2 py-0.5 rounded-full ${therapist.urgencyLevel === 'Highest' || therapist.urgencyLevel === 'High'
                ? 'bg-red-50 text-red-600'
                : 'bg-green-50 text-green-600'
                }`}>
                {therapist.urgencyLevel} Priority
              </div>
            )}
          </div>
        </div>

        {/* Rate & Availability Column */}
        <div className="col-span-3">
          <div className="text-right">
            <p className="text-xs text-gray-500 mb-1">
              {individualRate ? `$${individualRate}/session` : ''}
            </p>
            <p className="text-xs text-gray-500">{nextAvailable}</p>
          </div>
        </div>
      </div>

      {/* Bio Section - Desktop */}
      {therapist.profile?.bio && (
        <div className="hidden sm:block sm:mt-4 sm:pt-4 sm:border-t sm:border-gray-100/50">
          <button
            onClick={toggleBio}
            className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span>View Bio</span>
            {showBio ? (
              <ChevronUp className="w-3 h-3 transition-transform" />
            ) : (
              <ChevronDown className="w-3 h-3 transition-transform" />
            )}
          </button>
          {showBio && (
            <div className="mt-3 p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-700 leading-relaxed">{therapist.profile.bio}</p>
            </div>
          )}
        </div>
      )}

      {/* Desktop Actions */}
      <div className="hidden sm:flex sm:items-center sm:justify-between sm:mt-4 sm:pt-4 sm:border-t sm:border-gray-100/50">
        <div className="flex gap-2">
          {therapist.slug && (
            <button
              onClick={handleViewProfile}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
            >
              View Profile
            </button>
          )}
        </div>
        <button className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-xs py-2 px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
          Select Clinician
        </button>
      </div>
    </div>
  );
});

export default TherapistCard